/**
 * @owner @Appboy/intelligence
 */

import { PRESET_RULESET_TYPES } from "./types";
import type { PresetRulesetType } from "./types";

/** The QA agent types a preset ruleset can be offered for. */
export type QaPresetAgentType = "campaign_qa" | "canvas_qa";

/**
 * Describes a single preset QA ruleset category for the purpose of teaching
 * BrazeAI Operator how to categorize rules.
 */
export interface PresetRulesetMetadata {
  /** Short description of the kinds of rules that belong in this category. */
  useFor: string;
  /**
   * The extra reference context this category's rules receive at QA evaluation
   * time. Source of truth: `CampaignSetupAgentDefinition`
   * (`append_liquid_instructions`, `append_brand_guidelines`,
   * `campaign_summary_for_ruleset`).
   */
  context: string;
  /**
   * Which QA agent types offer this preset. Campaign QA and Canvas QA share most
   * presets but each has type-specific ones (e.g. `campaign_setup` is Campaign
   * QA only; `canvas_setup`/`canvas_structure` are Canvas QA only). Operator
   * instructions filter by this so a Campaign QA agent is never told about
   * Canvas-only presets and vice versa.
   */
  agentTypes: readonly QaPresetAgentType[];
}

const campaignAndCanvas: readonly QaPresetAgentType[] = ["campaign_qa", "canvas_qa"];
const campaignOnly: readonly QaPresetAgentType[] = ["campaign_qa"];
const canvasOnly: readonly QaPresetAgentType[] = ["canvas_qa"];

/**
 * Metadata for every preset ruleset type, keyed by its `type` identifier.
 *
 * Typed as `Record<PresetRulesetType, ...>` so this map is the single,
 * exhaustive source of truth for Operator's preset-category knowledge. Adding a
 * new preset type to `Braze::Agentic::Ruleset::PresetTypes` (which flows into
 * `PresetRulesetType` via shared_constants) becomes a compile error here until
 * the new category's `useFor`/`context` are documented — so the Operator tool
 * descriptions and instruction tables derived from this map can never silently
 * go stale.
 */
export const PRESET_RULESET_METADATA: Record<PresetRulesetType, PresetRulesetMetadata> = {
  [PRESET_RULESET_TYPES.CAMPAIGN_SETUP]: {
    useFor: "Naming, tags, schedule/timing, conversion events, control groups, entry/exit criteria",
    context: "Base campaign summary only",
    agentTypes: campaignOnly,
  },
  [PRESET_RULESET_TYPES.AUDIENCE_AND_TARGETING]: {
    useFor: "Target segments, exclusions/suppressions, audience size, filter logic",
    context: "Base campaign summary only",
    agentTypes: campaignAndCanvas,
  },
  [PRESET_RULESET_TYPES.CONTENT_AND_COPY]: {
    useFor: "Subject/preheader, copy quality, spelling/grammar, character limits, brand voice/tone",
    context:
      "**Brand guidelines** (when one is attached to the agent) are injected so the rules can check on-brand voice",
    agentTypes: campaignAndCanvas,
  },
  [PRESET_RULESET_TYPES.LINKS_AND_TRACKING]: {
    useFor: "CTAs/URLs, UTM parameters, malformed links, deep links, link destinations",
    context: "Base campaign summary only",
    agentTypes: campaignAndCanvas,
  },
  [PRESET_RULESET_TYPES.PERSONALIZATION_AND_DYNAMIC_CONTENT]: {
    useFor: "Liquid syntax, fallback/default values, conditional logic, content block references",
    context: "**Braze Liquid reference documentation** is injected so the rules can reason about Liquid correctly",
    agentTypes: campaignAndCanvas,
  },
  [PRESET_RULESET_TYPES.COMPLIANCE_AND_DELIVERABILITY]: {
    useFor:
      "Unsubscribe/preference center, legal disclaimers, transactional separation, spam/deliverability, accessibility",
    context:
      "An **accessibility-enriched campaign summary** is provided so the rules can check a11y/deliverability concerns",
    agentTypes: campaignAndCanvas,
  },
  [PRESET_RULESET_TYPES.CANVAS_SETUP]: {
    useFor: "Canvas-level naming, tags, entry schedule/timing, conversion events, entry/re-entry settings",
    context: "Base Canvas summary only",
    agentTypes: canvasOnly,
  },
  [PRESET_RULESET_TYPES.CANVAS_STRUCTURE]: {
    useFor: "Canvas flow: step connectivity, branching/decision logic, delays, exit criteria, dead ends",
    context: "Base Canvas summary only",
    agentTypes: canvasOnly,
  },
};

/**
 * Ordered list of [type identifier, metadata] entries for the preset categories.
 * Pass an `agentType` to restrict to presets offered for that QA agent type so,
 * e.g., Campaign QA Operator instructions never mention Canvas-only presets.
 */
export const presetRulesetEntries = (
  agentType?: QaPresetAgentType
): [PresetRulesetType, PresetRulesetMetadata][] => {
  const entries = Object.entries(PRESET_RULESET_METADATA) as [PresetRulesetType, PresetRulesetMetadata][];
  if (agentType === undefined) {
    return entries;
  }
  return entries.filter(([, metadata]) => metadata.agentTypes.includes(agentType));
};

/** Number of preset ruleset categories (optionally scoped to a QA agent type). */
export const presetRulesetCount = (agentType?: QaPresetAgentType): number => presetRulesetEntries(agentType).length;
